import { format } from 'date-fns';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import {
  denormalize,
  denormalizeToFormData,
  normalize,
} from '../shared/normalizers';
import { User } from '../user/_types';

import {
  PlanAssignment,
  PlanWorkout,
  WorkoutPlan,
  WorkoutPlanSearchResult,
} from './_types';
import {
  normalizePlanAssignment,
  normalizePlanBlock,
  normalizePlanWorkout,
  normalizeWorkoutPlan,
  normalizeWorkoutPlanSearchResult,
} from './normalizers';

export * from './_types';

interface FetchPlanAssignmentsOptions {
  userId?: Identifier;
  workoutPlanIds?: Identifier[];
  groupId?: Identifier;
  pluckColumns?: string;
  fetchAll?: boolean;
}

export const fetchPlanAssignments = async (
  params: FetchPlanAssignmentsOptions
): Promise<PlanAssignment[]> => {
  const response = await api.get('/api/v3/plan_assignments', {
    params: denormalize(params),
  });
  return response.data.map(normalizePlanAssignment);
};

export const deletePlanAssignments = async (id: Identifier) => {
  const response = await api.delete(`/api/v3/plan_assignments/${id}`);
  return response.data;
};

interface FetchWorkoutPlanOptions {
  id: Identifier;
}

export const fetchWorkoutPlan = async ({
  id,
}: FetchWorkoutPlanOptions): Promise<WorkoutPlan> => {
  const response = await api.get(`/api/v3/workout-plans/${id}`);
  return normalizeWorkoutPlan(response.data);
};

export const fetchWorkoutPlans = async (
  ids: Identifier[],
  params = {}
): Promise<WorkoutPlan[]> => {
  const response = await api.get('/api/v2/workout-plans', {
    params: { with_ids: ids.join(','), ...params },
  });
  return response.data.map(normalizeWorkoutPlan);
};

export const fetchWorkoutPlansByParams = async (
  params = {}
): Promise<WorkoutPlan[]> => {
  const response = await api.get('/api/v3/workout-plans', {
    params: { ...params },
  });
  return response.data.map(normalizeWorkoutPlan);
};

export const fetchWorkoutPlanOptions = async (): Promise<WorkoutPlan[]> => {
  const response = await api.get('/api/v2/workout-plans', {
    params: {
      user_created: true,
      fetch_all: true,
      pluck_columns: 'id,name,plan_type',
      q: {
        s: 'name asc',
      },
    },
  });
  return response.data.map(normalizeWorkoutPlan);
};

export const removeWorkoutPlanFromCalendar = async (
  workoutPlanId: Identifier,
  copiedAt: string
): Promise<WorkoutPlan> => {
  const response = await api.get(
    `/api/v4/workout-plans/${workoutPlanId}/remove_workouts/`,
    {
      params: { copied_at: copiedAt },
    }
  );

  return normalizeWorkoutPlan(response.data);
};

export const unassignPlan = async (assignmentId: Identifier): Promise<void> => {
  await api.delete(`/api/v4/plan_assignments/${assignmentId}`);
};

export interface ScheduledWorkout {
  id: string;
  text: string;
}

export const fetchScheduledWorkouts = async (
  userId: string
): Promise<ScheduledWorkout[]> => {
  const response = await api.get('/api/v4/calendar', {
    params: {
      user_id: userId,
      start: Math.floor(Date.now() / 1000), // current timestamp in seconds
      end: Math.floor(Date.now() / 1000), // same as start for today only
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
  return response.data;
};

interface WorkoutComplianceData {
  completed: number;
  workouts: number;
  percent: string;
}

interface FetchWorkoutComplianceParams {
  userId: string | number;
  startDate: number;
  endDate: number;
}

export const fetchWorkoutCompliance = async ({
  userId,
  startDate,
  endDate,
}: FetchWorkoutComplianceParams): Promise<WorkoutComplianceData> => {
  const response = await api.get(
    `/api/v3/users/${userId}/workout_compliance/`,
    {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    }
  );
  return response.data;
};
interface DeleteRepMaxParams {
  userId: User['id'];
  exerciseId: string;
  reps: string | number;
}

export const deleteRepMax = async ({
  userId,
  exerciseId,
  reps,
}: DeleteRepMaxParams) => {
  const response = await api.post(`/api/v3/users/${userId}/delete_rep_max/`, {
    exercise_id: exerciseId,
    reps: reps,
  });
  return response.data;
};

export const fetchAvailableWeekPlanOptions = async (): Promise<
  WorkoutPlan[]
> => {
  const response = await api.get('/api/v3/workout-plans/client_assignable', {
    params: {
      fetch_all: true,
      pluck_columns: 'id,name',
      week_only: true,
      show_drafts: true,
    },
  });
  return response.data.map(normalizeWorkoutPlan);
};

interface FetchPlanWorkoutsOptions {
  planId: Identifier;
}

export const fetchPlanWorkouts = async ({
  planId,
}: FetchPlanWorkoutsOptions): Promise<any[]> => {
  const response = await api.get('/api/v2/plan_workouts', {
    params: {
      plan_id: planId,
      fetch_all: true,
    },
  });
  return response.data;
};

export const fetchPlanWorkoutsById = async ({
  planId,
}: FetchPlanWorkoutsOptions): Promise<PlanWorkout[]> => {
  const response = await api.get('/api/v3/plan_workouts', {
    params: {
      plan_id: planId,
      fetch_all: true,
    },
  });
  return response.data.map(normalizeWorkoutPlan);
};

/**
 * Fetch plan workouts by their IDs
 * Used to fetch specific workouts associated with an appointment
 */
export const fetchPlanWorkoutsByIds = async (
  ids: Identifier[]
): Promise<PlanWorkout[]> => {
  if (!ids || ids.length === 0) {
    return [];
  }
  const response = await api.get('/api/v2/plan_workouts', {
    params: {
      with_ids: ids.join(','),
      pluck_columns: 'id,name,workout_plan_id',
    },
  });
  return response.data.map((w: any) => ({
    id: w.id,
    name: w.name,
    workoutPlanId: w.workout_plan_id,
  }));
};

export interface ImportWorkoutPlanToCalendarParams {
  workoutPlanId: Identifier;
  startDate: Date;
  workoutDays: number[];
  initialWorkoutId: string | null;
  repeatCount: number;
  preventNextWorkout: boolean;
  notifyClient: boolean;
  assignPlan: boolean;
  scheduleNext: boolean;
  userId: Identifier;
}

export const importWorkoutPlanToCalendar = async (
  params: ImportWorkoutPlanToCalendarParams
): Promise<void> => {
  await api.get(`/api/v3/workout-plans/${params.workoutPlanId}/copy_to_plan/`, {
    params: {
      plan_to_copy_id: params.workoutPlanId,
      start_date: format(params.startDate, 'yyyy-MM-dd'),
      initial_workout_id: params.initialWorkoutId,
      days: params.workoutDays.map((day, index) => index),
      schedule_next: params.scheduleNext,
      repeat_count: params.repeatCount,
      notify: params.notifyClient,
      prevent_next_workout: params.preventNextWorkout,
      assign_plan: params.assignPlan,
      assign_to_user_id: params.userId,
      schedule_plan: 'true',
    },
  });
};

export interface BulkImportWorkoutPlanToCalendarParams
  extends Omit<ImportWorkoutPlanToCalendarParams, 'userId'> {
  planIds: Identifier[];
  selectAll: boolean;
}

export const bulkImportWorkoutPlanToCalendar = async (
  params: BulkImportWorkoutPlanToCalendarParams
): Promise<void> => {
  await api.get(
    `/api/v3/workout-plans/${params.workoutPlanId}/bulk_copy_to_plan/`,
    {
      params: {
        plan_to_copy_id: params.workoutPlanId,
        start_date: format(params.startDate, 'MM/dd/yyyy'),
        initial_workout_id: params.initialWorkoutId,
        days: params.workoutDays.map((day, index) => index),
        schedule_next: params.scheduleNext,
        repeat_count: params.repeatCount,
        notify: params.notifyClient,
        prevent_next_workout: params.preventNextWorkout,
        assign_plan: params.assignPlan,
        plan_ids: params.planIds,
        all_pages: params.selectAll,
      },
    }
  );
};

interface FetchWorkoutPlansOptions {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  filters?: {
    trainerId?: string;
    isHeadTrainer?: boolean;
    type?: string;
    tags?: string[];
    matchAllTags?: boolean;
    difficultyLevel?: string[];
    planObjective?: string[];
    planType?: string[];
    day?: string[];
    wod?: string[];
    gender?: string[];
  };
  params?: Record<string, any>;
}

export const fetchWorkoutPlansQueryKey = (o: FetchWorkoutPlansOptions) => [
  'workoutPlans',
  o.sortBy,
  o.sortDir,
  o.page,
  o.perPage,
  o.search,
  o.filters,
];

export const searchWorkoutPlans = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  filters = {},
  params = {},
}: FetchWorkoutPlansOptions) => {
  // Convert type to appropriate query parameters
  const typeParams = (() => {
    if (!filters.type) return {};

    switch (filters.type.toLowerCase()) {
      case 'client plans':
        return { 'q[has_clients]': true };
      case 'plans for sale':
        return { 'q[price_gt]': 0 };
      case 'drafts':
        return { 'q[status_eq]': 0 };
      default:
        return {};
    }
  })();

  const response = await api.get(`/api/v3/workout-plans`, {
    params: {
      page,
      per: perPage,
      summary_only: true,
      user_created: true,
      ...(search && { 'q[name_cont]': search }),
      ...(sortBy && {
        'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
      }),
      ...typeParams, // Use the converted type parameters
      ...(filters.trainerId &&
        !filters.isHeadTrainer && {
          'q[sub_trainer_id_eq]': filters.trainerId,
        }),
      ...(filters.isHeadTrainer && { 'q[sub_trainer_id_null]': 1 }),
      // Without Match all tags
      ...(!filters.matchAllTags &&
        filters.tags && {
          'q[tags_name_eq_any]': filters.tags,
        }),
      // With Match all tags
      ...(filters.matchAllTags &&
        filters.tags && { 'q[workout_plan_tags]': filters.tags.join(',') }),
      ...(filters.difficultyLevel && {
        'q[difficulty_level_id_in]': filters.difficultyLevel,
      }),
      ...(filters.planObjective && {
        'q[purposes_id_in]': filters.planObjective,
      }),
      ...(filters.planType && {
        'q[premium_in]': filters.planType,
      }),
      ...(filters.day && {
        'q[has_days_per_week]': filters.day,
      }),
      ...(filters.wod && {
        'q[wod_eq]': filters.wod,
      }),
      ...(filters.gender && {
        'q[gender_in]': filters.gender,
      }),
      ...params,
    },
  });

  const normalizedPlans = response.data?.workout_plan?.map((plan) =>
    normalize(plan)
  );

  return { plans: normalizedPlans, total: response.data?.meta?.total };
};

export const updateWorkoutPlanTags = async (data: {
  workoutPlanId: Identifier;
  tags: string;
}) => {
  try {
    const response = await api.post(
      `/api/v3/workout-plans/update_tags`,
      denormalizeToFormData(data)
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      api.formatError(error, 'Error updating workout plan tags.')
    );
  }
};

export const cloneWorkoutPlan = async (workoutPlanId: Identifier) => {
  try {
    const response = await api.get(
      `/api/v2/workout-plans/${workoutPlanId}/clone/`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error cloning workout plan.'));
  }
};

export const deleteWorkoutPlan = async (workoutPlanId: Identifier) => {
  try {
    const response = await api.delete(`/api/v3/workout-plans/${workoutPlanId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error deleting workout plan.'));
  }
};

export const bulkUpdateWorkoutPlan = async (data) => {
  try {
    const response = await api.post(
      `/api/v2/workout-plans/bulk_update`,
      denormalizeToFormData(data)
    );
    return response.data;
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error updating workout plan.'));
  }
};

export const mergeWorkoutPlans = async (data) => {
  try {
    const response = await api.post(
      `/api/v3/workout-plans/merge_plans`,
      denormalizeToFormData(data)
    );
    return response.data;
  } catch (error: any) {
    const errors = error.response?.data?.errors;
    if (typeof errors === 'string') {
      throw new Error(errors);
    }
    throw new Error(api.formatError(error, 'Error merging workout plans.'));
  }
};

export const updatePlanSort = async (planIds: Identifier[]) => {
  const response = await api.put(`/api/v3/workout-plans/update_plan_sort`, {
    plans: planIds.map((id) => id.toString()),
  });
  return response.data;
};

export const removePlanSort = async (planIds: Identifier[]) => {
  const response = await api.put(`/api/v3/workout-plans/remove_plan_sort`, {
    plans: planIds.map((id) => id.toString()),
  });
  return response.data;
};

export const updateWorkoutPlan = async (
  id: Identifier,
  data: Partial<WorkoutPlan>
): Promise<WorkoutPlan> => {
  const response = await api.put(`/api/v3/workout-plans/${id}`, {
    workout_plan: denormalize(data),
  });
  return normalizeWorkoutPlan(response.data);
};

export const savePlanAssignment = async (
  data: Partial<PlanAssignment>
): Promise<PlanAssignment> => {
  if (data.id) {
    const response = await api.put(`/api/v3/plan_assignments/${data.id}`, {
      plan_assignment: denormalize(data),
    });
    return normalizePlanAssignment(response.data);
  } else {
    const response = await api.post('/api/v3/plan_assignments', {
      plan_assignment: denormalize(data),
    });
    return normalizePlanAssignment(response.data);
  }
};

export const searchWorkoutPlanResults = async ({
  search,
  trainerId,
  signal,
}: {
  search: string;
  trainerId: Identifier;
  signal?: AbortSignal;
}): Promise<WorkoutPlanSearchResult[]> => {
  const response = await api.get('/sm/search', {
    signal,
    params: {
      types: [`workoutplan_white_label_${trainerId}`],
      limit: 20,
      no_limit: false,
      term: search,
      page: 1,
    },
  });
  return response.data.results[`workoutplan_white_label_${trainerId}`].map(
    (r: WorkoutPlanSearchResult) => normalizeWorkoutPlanSearchResult(r)
  );
};

export const fetchPlanWorkout = async (workoutId: Identifier) => {
  const response = await api.get(`/api/v3/plan_workouts/${workoutId}`);
  return normalizePlanWorkout(response.data);
};

export const fetchPlanBlock = async (blockId: Identifier) => {
  const response = await api.get(`/api/v3/plan_blocks/${blockId}`);
  return normalizePlanBlock(response.data);
};
